import { UrlQueryObject } from 'services/url/types';

export enum TransferOrderStatus {
  Draft = 'Draft',
  Picked = 'Picked',
  InTransit = 'In Transit',
  Received = 'Received',
  Complete = 'Complete',
  PartialTransfer = 'Partial Transfer',
  Cancelled = 'Cancelled',
}

export enum TransferOrderType {
  MTO = 'MTO',
  STO = 'STO',
}

export interface TransferOrder {
  id: number | null;
  number: string | null;
  status: TransferOrderStatus | null;
  type: TransferOrderType | null;
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  sourceLocation: {
    id: number;
    name: string;
  } | null;
  destinationLocation: {
    id: number;
    name: string;
  } | null;
  transferReason: string | null;
  dateCreated: Date | null;
  dateLastModified: Date | null;
  dateIssued: Date | null;
  dateFulfilled: Date | null;
  deleted: boolean;
  totalQuantity: number | null;
  totalItems: number | null;
  createdByUserId: number | null;
  lastModifiedByUserId: number | null;
  items: TransferOrderItem[];
  attachments: TransferOrderAttachment[];
  notes: TransferOrderNote[];
  auditLog: TransferOrderAuditLog[];
}

export enum TransferOrderItemStatus {
  Entered = 'Entered',
  Picking = 'Picking',
  PartiallyPicked = 'Partially Picked',
  Picked = 'Picked',
  PartiallyFulfilled = 'Partially Fulfilled',
  Fulfilled = 'Fulfilled',
  ClosedShort = 'Closed Short',
  Voided = 'Voided',
}

export interface TransferOrderItem {
  id: number | null;
  transferOrderId: number | null;
  itemId: number | null;
  item: {
    id: number;
    name: string;
    sku: string;
    description: string | null;
  } | null;
  quantityOrdered: number | null;
  quantityFulfilled: number | null;
  unitPrice: number | null;
  subtotal: number | null;
  status: TransferOrderItemStatus | null;
  accountingClassId: number | null;
  accountingClass: {
    id: number;
    name: string;
  } | null;
  uomId: number | null;
  uom: {
    id: number;
    name: string;
    abbreviation: string;
  } | null;
  lineNumber: number | null;
  notes: string | null;
  deleted: boolean;
  dateCreated: Date | null;
  dateLastModified: Date | null;
}

export interface TransferOrderAttachment {
  id: number | null;
  transferOrderId: number | null;
  fileName: string | null;
  filePath: string | null;
  fileSize: number | null;
  mimeType: string | null;
  dateCreated: Date | null;
  createdByUserId: number | null;
  createdBy: {
    id: number;
    name: string;
  } | null;
  deleted: boolean;
}

export interface TransferOrderNote {
  id: number | null;
  transferOrderId: number | null;
  content: string | null;
  dateCreated: Date | null;
  dateLastModified: Date | null;
  createdByUserId: number | null;
  lastModifiedByUserId: number | null;
  createdBy: {
    id: number;
    name: string;
  } | null;
  deleted: boolean;
}

export enum AuditLogAction {
  Created = 'Created',
  Updated = 'Updated',
  Issued = 'Issued',
  Unissued = 'Unissued',
  StatusChanged = 'Status Changed',
  TypeChanged = 'Type Changed',
  ItemAdded = 'Item Added',
  ItemRemoved = 'Item Removed',
  ItemUpdated = 'Item Updated',
  PickCompleted = 'Pick Completed',
  PickVoided = 'Pick Voided',
  ShipmentCreated = 'Shipment Created',
  ShipmentVoided = 'Shipment Voided',
  ReceiptCompleted = 'Receipt Completed',
  ReceiptVoided = 'Receipt Voided',
  Deleted = 'Deleted',
  SyncedToQBO = 'Synced to QBO',
  AttachmentUploaded = 'Attachment Uploaded',
  AttachmentDeleted = 'Attachment Deleted',
  NoteAdded = 'Note Added',
  NoteUpdated = 'Note Updated',
  NoteDeleted = 'Note Deleted',
}

export interface TransferOrderAuditLog {
  id: number | null;
  transferOrderId: number | null;
  action: AuditLogAction | null;
  details: string | null;
  oldValue: string | null;
  newValue: string | null;
  dateCreated: Date | null;
  createdByUserId: number | null;
  createdBy: {
    id: number;
    name: string;
  } | null;
  metadata: Record<string, unknown> | null;
}

export interface CreateTransferOrderRequest {
  sourceLocationId: number;
  destinationLocationId: number;
  transferReason?: string;
  items: CreateTransferOrderItemRequest[];
}

export interface CreateTransferOrderItemRequest {
  itemId: number;
  quantity: number;
  accountingClassId: number;
  uomId?: number;
  notes?: string;
}

export interface UpdateTransferOrderRequest {
  sourceLocationId?: number;
  destinationLocationId?: number;
  transferReason?: string;
  items?: UpdateTransferOrderItemRequest[];
}

export interface UpdateTransferOrderItemRequest {
  id?: number;
  itemId: number;
  quantity: number;
  accountingClassId: number;
  uomId?: number;
  notes?: string;
  deleted?: boolean;
}

export interface AddTransferOrderItemRequest {
  transferOrderId: number;
  itemId: number;
  quantity: number;
  accountingClassId: number;
  uomId?: number;
  notes?: string;
}

export interface TransferOrderFormData {
  sourceLocationId: number | null;
  destinationLocationId: number | null;
  transferOrderNumber: string | null;
  transferReason: string;
  items: TransferOrderFormItemData[];
  isEditing: boolean;
  isDirty: boolean;
}

export interface TransferOrderFormItemData {
  tempId: string;
  id?: number;
  itemId: number | null;
  item?: {
    id: number;
    name: string;
    sku: string;
    description: string | null;
  };
  quantity: number | null;
  accountingClassId: number | null;
  accountingClass?: {
    id: number;
    name: string;
  };
  uomId?: number | null;
  uom?: {
    id: number;
    name: string;
    abbreviation: string;
  };
  notes: string;
  isDeleted: boolean;
  isNew: boolean;
  errors?: {
    itemId?: string;
    quantity?: string;
    accountingClassId?: string;
  };
}

export interface GetTransferOrdersResponse {
  data: DisplayTransferOrder[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface DisplayTransferOrder {
  id: number;
  number: string;
  status: TransferOrderStatus;
  type: TransferOrderType;
  sourceName: string;
  destinationName: string;
  /** ISO 8601 UTC string, e.g., 2025-09-09T19:37:34Z */
  lastUpdated: string;
  totalQuantity: number;
  totalItems: number;
  deleted: boolean;
}

export interface GetTransferOrderResponse {
  data: TransferOrder;
}

export interface CreateTransferOrderResponse {
  data: TransferOrder;
  message: string;
}

export interface UpdateTransferOrderResponse {
  data: TransferOrder;
  message: string;
}

export interface TransferOrderActionResponse {
  data: TransferOrder;
  message: string;
  actionData?: {
    pickId?: number;
    shipmentId?: number;
    receiptId?: number;
  };
}

export interface TransferOrderQueryParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: TransferOrderStatus[];
  type?: TransferOrderType[];
  sourceLocationId?: number[];
  destinationLocationId?: number[];
  dateFrom?: string;
  dateTo?: string;
  sortBy?:
    | 'number'
    | 'status'
    | 'sourceName'
    | 'destinationName'
    | 'lastUpdated';
  sortOrder?: 'asc' | 'desc';
  includeDeleted?: boolean;
}

export interface AddTransferOrderAttachmentResponse {
  data: TransferOrderAttachment;
  message: string;
}

export interface AddTransferOrderNoteResponse {
  data: TransferOrderNote;
  message: string;
}

export interface GenerateTransferOrderNumberResponse {
  number: string;
  type: TransferOrderType;
}

export interface GenerateTransferOrderNumberRequest {
  sourceLocationId: number;
  destinationLocationId: number;
}

export interface TransferOrderErrorResponse {
  message: string;
  code?: string;
  details?: string;
  validationErrors?: {
    field: string;
    message: string;
  }[];
}

export enum TransferOrderTab {
  General = 'general',
  Attachments = 'attachments',
  Notes = 'notes',
}

export interface TransferOrderUrlQueryObject extends UrlQueryObject {
  activeTab?: TransferOrderTab;
}
